// In-portal Help — renders as a modal overlay on top of the prototype list,
// not a separate route. The hash routing (#help) drives open/close so the
// URL stays shareable and back-button works.
//
// SPLIT BY AUDIENCE. The page serves two groups who need almost nothing in
// common: people who upload something and want to control who sees it, and
// people wiring a prototype up to the SDK. It used to serve only the second —
// six sections on the skill, /__auth/me, the KV store and CI tokens, and
// essentially nothing on the sharing and access-control model that most of the
// product now is. Someone reading it would not have learned that restricted
// links exist, that they are bearer URLs, or that they can be gated by email
// domain. That gap matters more than it looks: the sharing copy elsewhere is
// worded carefully to avoid overstating what a gate buys, and help that omits
// the model is the same failure at a larger scale.
//
// #help opens the sharing tab; #help-dev links straight to the developer tab.

export interface PortalConfig {
  apiUrl: string;
  // OAuth values for the rewind-protos skill's CLI client. Populated from
  // SSM at deploy time. The skill reads these on every login (instead of
  // bundling them into the zip).
  googleCliClientId: string;
  googleCliClientSecret: string;
  skillVersion: string;
  skillUrl: string;
  // Served in /config.json (lib/portal-stack.ts) and rendered into the help
  // copy, so the documented retention count cannot drift from the enforced one.
  maxRetainedVersions: number;
  uploadLimits: {
    maxZipBytes: number;
    maxExtractedBytes: number;
    maxEntryBytes: number;
    maxEntries: number;
    maxPathDepth: number;
    allowedExtensions: string[];
  };
}

export type HelpTab = "sharing" | "dev";

/** #help-dev selects the developer tab; anything else lands on sharing. */
export function helpTabFromHash(hash: string): HelpTab {
  return hash === "#help-dev" ? "dev" : "sharing";
}

export function hashForHelpTab(tab: HelpTab): string {
  return tab === "dev" ? "#help-dev" : "#help";
}

function fmtMB(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

// Escape strings interpolated into innerHTML. /config.json is server-controlled
// so values are trusted today, but defense-in-depth in case future config sources
// (URL params, user-provided overrides, etc.) ever feed into renderHelp.
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ---- Tab 1: sharing ----------------------------------------------------

function sharingPanel(cfg: PortalConfig): string {
  const allowedExtsSafe = esc(cfg.uploadLimits.allowedExtensions.join(", "));
  return `
      <h3>Uploading</h3>
      <ul>
        <li>Click <strong>Upload</strong>, give it a title, choose your ZIP.</li>
        <li>Limits: ${fmtMB(cfg.uploadLimits.maxZipBytes)} zip,
          ${fmtMB(cfg.uploadLimits.maxExtractedBytes)} extracted,
          ${Number(cfg.uploadLimits.maxEntries)} files max,
          ${Number(cfg.uploadLimits.maxPathDepth)} levels deep,
          ${fmtMB(cfg.uploadLimits.maxEntryBytes)} per file.</li>
        <li>Allowed file types: ${allowedExtsSafe}</li>
        <li>A single HTML file works too — you don't have to zip one page.</li>
      </ul>

      <h3>Who can see it</h3>
      <p>Every prototype is one of three things. <strong>New uploads are
      Internal</strong> unless you change them.</p>
      <ul>
        <li><strong>Internal</strong> (default) — anyone signed in with a Rewind
          Google account can open it. Nobody outside Rewind can, even with the
          URL.</li>
        <li><strong>Public</strong> — anyone with the URL can open it, no
          sign-in. There is no other protection, so treat the URL itself as the
          only thing standing between the content and the internet.</li>
        <li><strong>Restricted</strong> — nobody can open the normal URL. Access
          is only through <em>share links</em> you create and can revoke
          individually. This is what to use for anyone outside Rewind.</li>
      </ul>

      <h3>Share links</h3>
      <p>On a Restricted prototype, the <strong>Restricted access links</strong>
      band lets you create a named link per recipient. Give each one a name you
      will recognise later — the name is only for you, recipients never see
      it.</p>
      <p><strong>A share link is a password.</strong> Anyone holding the URL can
      open the content — there is no sign-in behind it, so a forwarded link
      works for whoever receives it. Give each recipient their own link, so you
      can revoke one without disturbing the others.</p>
      <p><strong>Revoking takes effect within a few seconds</strong> and cannot
      be undone — issue a new link instead. Revoke is the immediate control;
      everything else on this page is a softer one.</p>

      <h3>Requiring email verification</h3>
      <p>A share link can additionally require the recipient to prove they hold
      an email address at a domain you nominate. Tick <strong>Require recipients
      to verify their email address</strong> when creating a link, or use the
      pencil on an existing row to add, change, or remove the list at any
      time — including on a link you have already sent out.</p>
      <p>When it is on, opening the link asks for an email address, sends a
      six-digit code to it, and only then shows the content. The code expires
      after ten minutes and stops working after five wrong attempts. Once
      verified, that person stays verified on that device for about a week.</p>
      <p><strong>What this does and does not buy you.</strong> It means someone
      holding the link cannot open it without also receiving mail at one of your
      domains. It does <em>not</em> stop a verified recipient forwarding the
      content, and it cannot tell a person from a shared mailbox like
      <code>support@acme.com</code>. The unit of trust is "controls an address
      at this domain", not "is this specific person".</p>
      <ul>
        <li>Domains are matched exactly. <code>acme.com</code> does not cover
          <code>mail.acme.com</code> — add each one. Up to
          ${Number(10)} per link.</li>
        <li>Changing or clearing the list does not sign out anyone who has
          already verified; they keep access until their week runs out.
          <strong>Revoke the link</strong> if you need access cut off now.</li>
        <li>The domains are never shown to recipients, and the page looks
          identical whether or not an address is allowed — so the link cannot be
          used to work out who you shared with.</li>
      </ul>

      <h3>Friendly URLs</h3>
      <p>Give a prototype a memorable name and share
      <code>/your-name</code> instead of the generated slug. A friendly URL can
      follow the latest version automatically, or stay pinned to one version so
      a link in a doc keeps showing what it showed when you wrote it.</p>

      <h3>Versions, retention, and ownership</h3>
      <ul>
        <li>Uploading again adds a <strong>version</strong> rather than
          replacing anything. The newest
          ${Number(cfg.maxRetainedVersions)} are kept.</li>
        <li>Prototypes are deleted after <strong>90 days</strong> unless you
          click <strong>Keep</strong>. A prototype must be kept before it can
          have share links, since the links would otherwise outlive the
          content.</li>
        <li><strong>Transfer</strong> hands a prototype and all of its share
          links to someone else — for when you are leaving a project rather than
          deleting the work. The links keep working; the new owner controls
          them.</li>
        <li>Tags, search, and the <strong>Mine / All</strong> filter are there
          for finding things once you have more than a handful.</li>
      </ul>`;
}

// ---- Tab 2: developers -------------------------------------------------

function devPanel(cfg: PortalConfig): string {
  // Use the stable /skill-latest.zip alias so users (and anyone copy-pasting
  // this command into a Slack message or Confluence) don't end up with a URL
  // that 404s on the next version bump. The "Current skill version" line below
  // still shows the version explicitly for transparency.
  const portalOrigin = window.location.origin;
  const installUrl = `${portalOrigin}/skill-latest.zip`;
  const apiUrlSafe = esc(cfg.apiUrl);
  const skillVersionSafe = esc(cfg.skillVersion);

  // The install one-liner below downloads to /tmp first, then nukes the old
  // install dir before extracting. This ordering matters:
  // 1. Download-first means a network failure leaves the old install intact
  //    (not a missing skill dir).
  // 2. The nuke-before-extract ensures `unzip -o` starts clean — without it,
  //    files dropped between versions (e.g. v0.1.x's lib/oauth-config.json)
  //    linger as orphans because unzip only overwrites, never deletes.
  // Safe because credentials live in ~/.config/rewind-protos/, not the skill dir.
  return `
      <h3>Use Claude Code (recommended)</h3>

      <h4>1. Install / upgrade</h4>
      <p>Paste in a terminal:</p>
      <pre><code>curl -fsSL ${installUrl} -o /tmp/rewind-protos-skill.zip &amp;&amp; \\
  rm -rf ~/.claude/skills/rewind-protos &amp;&amp; \\
  mkdir -p ~/.claude/skills/rewind-protos &amp;&amp; cd ~/.claude/skills/rewind-protos &amp;&amp; \\
  unzip -o /tmp/rewind-protos-skill.zip &amp;&amp; rm /tmp/rewind-protos-skill.zip</code></pre>

      <h4>2. Log in (one-time per machine)</h4>
      <p>In Claude Code, ask: <em>"log in to Holo"</em> (or <em>"log in to the prototype portal"</em>). A browser opens, sign in with your Rewind Google account (<code>@rewind.io</code> / <code>@rewind.com</code>). Refresh token is stored at <code>~/.config/rewind-protos/credentials.json</code> (mode 0600).</p>

      <h4>3. Sample commands you can ask Claude</h4>
      <ul>
        <li><em>"list my shares"</em> — lists shares you own with their viewer URLs</li>
        <li><em>"list all shares"</em> — same but includes everyone's, with owner column</li>
        <li><em>"push the folder at ./hot-standby to Holo titled 'Hot Standby'"</em> — creates a brand-new share</li>
        <li><em>"push that update"</em> — pushes a new version of the same folder; slug is auto-detected from <code>./hot-standby/.rewind-protos.json</code></li>
        <li><em>"push that update with note 'fixed the nav layout'"</em> — same, with a custom version note (default is <em>"Updated via Claude skill"</em>)</li>
        <li><em>"log out of Holo"</em> — clears stored credentials</li>
        <li><em>"upgrade rewind-protos"</em> — re-installs the latest version (also see "Updating" below)</li>
      </ul>
      <p>Works with the <code>/prototype</code> skill — that skill calls <code>publish.mjs</code> automatically when this one is installed, so you can use either entry point.</p>

      <h4>4. Updating the skill</h4>
      <p>The skill checks its bundled version against the deployed version on every command. When out of date, it prints a one-line warning to stderr that Claude surfaces back to you, like:</p>
      <pre><code>⚠ rewind-protos vX.Y.Z is outdated (latest: ${skillVersionSafe}). Update: ${portalOrigin}/#help-dev</code></pre>
      <p>To upgrade, ask Claude <em>"upgrade rewind-protos"</em> — Claude will re-run the install one-liner above. Your existing credentials carry over (no need to re-login). The same one-liner installs and upgrades because <code>skill-latest.zip</code> always points at the current build.</p>

      <p><small>Current deployed skill version: <strong>${skillVersionSafe}</strong></small></p>

      <h3>Reading the signed-in user</h3>
      <p>Internal share pages are always behind Rewind sign-in, but the session
      cookie is <code>HttpOnly</code> so your page's JS can't read it directly.
      Fetch <code>/__auth/me</code> (same-origin) to get the signed-in viewer —
      no need to wire up your own Google SSO:</p>
      <pre><code>(async () =&gt; {
  const res = await fetch("/__auth/me");
  if (res.ok) {
    const { email, hd } = await res.json();
    const name = emailToDisplayName(email); // see below — "Dave North"
    // render a per-user view, greet the user by name, etc.
  } else if (res.status === 401) {
    // no valid session — bounce through sign-in and return to this exact URL
    const returnTo = location.pathname + location.search + location.hash;
    location.href = "/__auth/start?return_to=" + encodeURIComponent(returnTo);
  }
})();</code></pre>
      <p>The response is <code>{ email, hd }</code> JSON on a 200 (<code>hd</code> is
      the Google hosted domain, e.g. <code>rewind.io</code>), or <code>401</code>
      when there's no valid session. The session cookie is scoped to the whole
      viewer domain, so <strong>any signed-in Rewind user gets a 200</strong> —
      even while viewing a public share. A <code>401</code> means no session at
      all: an anonymous visitor, or a session that has expired. (Internal share
      pages are always behind sign-in, so you'll get a 200 there on load.)</p>
      <p><strong>On a restricted share</strong> (<code>/s/&lt;id&gt;/</code>) the
      viewer may not be a Rewind user at all, so treat a <code>401</code> as
      normal and do not bounce them to sign-in — they were let in by the share
      link, and a redirect would strand them.</p>
      <p><strong>Getting the real name.</strong> The endpoint returns email + hd,
      not a name — but Rewind emails are <code>first.last@rewind.io</code>, so you
      can derive a display name from the email (this is what the desk-booker
      prototype does):</p>
      <pre><code>function emailToDisplayName(email) {
  return email.split("@")[0]        // "dave.north"
    .split(/[._-]+/)                // ["dave", "north"]
    .filter(Boolean)
    .map(w =&gt; w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");                     // "Dave North"
}</code></pre>
      <p>It's a heuristic — it works for the standard <code>first.last</code> form
      and falls back to a reasonable guess for anything else (a shared mailbox
      like <code>team@rewind.io</code> becomes <code>Team</code>).</p>

      <h3>Persisting data (KV store)</h3>
      <p>Internal shares can persist and read back small JSON values from
      client-side JS — handy for votes, saved settings, demo state, etc. It's
      gated on sign-in: <strong>public shares get a 403</strong>. Data is
      scoped to the share; other shares can't read it.</p>
      <p>Add the SDK with one line (it uses your existing viewer sign-in cookie
      automatically):</p>
      <pre><code>&lt;script src="https://p.ai.rewind.engineering/__sdk/kv.js"&gt;&lt;/script&gt;</code></pre>
      <p>Then use <code>window.rewindKV</code>:</p>
      <pre><code>await rewindKV.set("votes", { pizza: 3, sushi: 1 });
const got = await rewindKV.get("votes");                       // { value, version } or null
await rewindKV.set("votes", got.value, { ifVersion: got.version }); // optimistic; throws RewindKVError (409) on conflict
await rewindKV.list();                                         // [{ key, version, bytes, updatedAt }]
await rewindKV.delete("votes");</code></pre>
      <p>Limits: 256&nbsp;KB per value, 200 keys per share, keys are
      <code>[A-Za-z0-9._-]</code> up to 128 characters.</p>

      <h3>Programmatic / CI (per-share token)</h3>
      <p>For non-Claude automation: click <strong>Generate Upload Token</strong> on any share you own (8h TTL, scoped to that share only).</p>
      <details>
        <summary>Show curl example</summary>
        <pre><code># 1. Create a new version (returns presigned upload URL)
curl -X POST ${apiUrlSafe}/prototypes/SLUG/versions \\
  -H "Authorization: ProtoToken YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"note":"Updated nav","uploadedBy":"ci-job-42"}'

# 2. Upload the ZIP to the returned uploadUrl using the returned fields
# (multipart/form-data; see response.fields for the exact field set)</code></pre>
      </details>`;
}

// ---- Render ------------------------------------------------------------

// role="tab" alone is not the tab pattern. Without aria-controls the panel is
// not associated with the tab that opened it, and without tabindex="-1" on the
// inactive tab both land in the tab order — so a keyboard user tabs THROUGH the
// tab strip instead of arrowing within it, which is the opposite of what the
// role advertises to assistive tech.
function tabButton(tab: HelpTab, active: HelpTab, label: string, hint: string): string {
  const on = tab === active;
  return `<button type="button" class="help-tab${on ? " help-tab--active" : ""}"
    role="tab" id="help-tab-${tab}" aria-controls="help-panel"
    aria-selected="${on}" tabindex="${on ? "0" : "-1"}" data-tab="${tab}">
    <span class="help-tab-label">${label}</span>
    <span class="help-tab-hint">${hint}</span>
  </button>`;
}

export function renderHelp(root: HTMLElement, cfg: PortalConfig, tab: HelpTab = "sharing"): void {
  root.innerHTML = `
    <section class="card help-view">
      <h2>Help</h2>
      <div class="help-tabs" role="tablist">
        ${tabButton("sharing", tab, "Sharing your work", "Uploading, who can see it, share links")}
        ${tabButton("dev", tab, "For developers", "Claude Code, the SDK, CI")}
      </div>
      <div class="help-panel" role="tabpanel" id="help-panel"
           aria-labelledby="help-tab-${tab}" tabindex="0">
        ${tab === "dev" ? devPanel(cfg) : sharingPanel(cfg)}
      </div>
    </section>
  `;

  const select = (next: HelpTab) => {
    if (next === tab) return;
    // replaceState, not pushState: switching tabs should not add a history
    // entry, or Back would walk the user through their tab clicks instead of
    // leaving the modal. The hash still updates so the view stays linkable.
    history.replaceState(
      "",
      document.title,
      location.pathname + location.search + hashForHelpTab(next),
    );
    renderHelp(root, cfg, next);
    root.querySelector<HTMLButtonElement>(`.help-tab[data-tab="${next}"]`)?.focus();
  };

  for (const btn of root.querySelectorAll<HTMLButtonElement>(".help-tab")) {
    btn.addEventListener("click", () => select((btn.dataset.tab as HelpTab) ?? "sharing"));
  }

  // Arrow keys move between tabs. This is REQUIRED, not a nicety: the inactive
  // tab carries tabindex="-1" (that is what keeps the tab strip out of the
  // document tab order), so without this the second tab would be unreachable by
  // keyboard entirely — a worse outcome than the flat tab order it replaced.
  root.querySelector<HTMLElement>(".help-tabs")?.addEventListener("keydown", (e) => {
    const key = (e as KeyboardEvent).key;
    const order: HelpTab[] = ["sharing", "dev"];
    let next: HelpTab | undefined;
    if (key === "ArrowRight" || key === "ArrowDown") {
      next = order[(order.indexOf(tab) + 1) % order.length];
    } else if (key === "ArrowLeft" || key === "ArrowUp") {
      next = order[(order.indexOf(tab) + order.length - 1) % order.length];
    } else if (key === "Home") {
      next = order[0];
    } else if (key === "End") {
      next = order[order.length - 1];
    }
    if (!next) return;
    e.preventDefault();
    select(next);
  });
}

// ---- Modal lifecycle ----

const MODAL_ID = "help-modal";
// Element to restore focus to when the modal closes. Saved on open.
let previouslyFocused: Element | null = null;

/**
 * Mount the help modal as an overlay on top of the existing page.
 * Idempotent — calling twice does nothing.
 *
 * Close behavior: click overlay backdrop, click × button, or press Escape.
 * Each closure removes `#help` from the URL hash (via replaceState — see
 * clearHelpHash) so the back button doesn't get a "reopen modal" entry.
 */
export function openHelpModal(cfg: PortalConfig, tab: HelpTab = "sharing"): void {
  if (document.getElementById(MODAL_ID)) return; // already open

  // Save the element that had focus before the modal opened so we can
  // restore it on close (a11y requirement for modal dialogs).
  previouslyFocused = document.activeElement;

  const overlay = document.createElement("div");
  overlay.id = MODAL_ID;
  overlay.className = "modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Help");

  const dialog = document.createElement("div");
  dialog.className = "modal";
  // Stop overlay click from bubbling — we close on overlay click but NOT on
  // dialog click (so users can interact with the dialog content).
  dialog.addEventListener("click", (e) => e.stopPropagation());

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "modal-close";
  closeBtn.setAttribute("aria-label", "Close help");
  closeBtn.innerHTML = "&times;";
  closeBtn.addEventListener("click", clearHelpHash);

  const content = document.createElement("div");
  content.className = "modal-content";
  renderHelp(content, cfg, tab);

  dialog.append(closeBtn, content);
  overlay.append(dialog);
  overlay.addEventListener("click", clearHelpHash);
  document.addEventListener("keydown", onKeydown);
  document.body.append(overlay);
  document.body.classList.add("modal-open");

  // Focus the close button so Escape / Enter just work and screen readers
  // announce the dialog.
  closeBtn.focus();
}

export function closeHelpModal(): void {
  const overlay = document.getElementById(MODAL_ID);
  if (!overlay) return;
  document.removeEventListener("keydown", onKeydown);
  overlay.remove();
  document.body.classList.remove("modal-open");

  // Restore focus to whatever had it before we opened.
  if (previouslyFocused instanceof HTMLElement) {
    previouslyFocused.focus();
  }
  previouslyFocused = null;
}

// Keyboard handlers while the modal is open: Escape closes; Tab/Shift+Tab
// cycle within the dialog (focus trap so tabbing can't reach content
// behind the overlay — a11y requirement for modal dialogs).
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    clearHelpHash();
    return;
  }
  if (e.key !== "Tab") return;
  const overlay = document.getElementById(MODAL_ID);
  if (!overlay) return;
  const focusables = overlay.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea, summary, details',
  );
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement as HTMLElement | null;
  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

function clearHelpHash() {
  // Use replaceState (not pushState) so closing the modal doesn't add a
  // history entry — otherwise pressing Back after closing would navigate
  // back to #help and reopen the modal (opposite of the intended UX).
  // We dispatch hashchange manually because replaceState doesn't fire it.
  //
  // Both help hashes must be handled: #help-dev is a real entry point (it is
  // in the skill's out-of-date warning), and matching only #help would leave
  // the modal open with no way to close it from that tab.
  if (location.hash === "#help" || location.hash === "#help-dev") {
    history.replaceState("", document.title, location.pathname + location.search);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  } else {
    closeHelpModal();
  }
}
